import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import { FormattedMessage, useIntl } from 'react-intl';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { GatsbyImage } from "gatsby-plugin-image";
import icon from 'components/image/headerIcon';
import LanguageSwitcher from 'components/languageSwitcher/languageSwitcher';
import { StateContext } from 'globalState';
import { Language, PageType } from 'enums/types';
import type { NavigationLink, NavigationSection } from 'components/header/header';
import { routes } from 'languages';
import { ibLogin, ibSignUp } from 'helpers/ibLinks';
import { isEdge, isIE, isSafari } from '../../browserHelper';
import * as style from './menu.module.scss';

interface ExternalLink {
	title: string;
	link: string;
	subtitle: string;
}

interface ExternalProductsLink {
	title: string;
	subtitle: string;
	link: string;
	icon?: string;
}

interface Props {
	navigation: NavigationSection[];
	externalProducts: ExternalLink[];
	showLanguageSwitcher?: boolean;
	originalPath: string;
	pageType: PageType;
}

const MobileNav = ({ navigation, externalProducts, showLanguageSwitcher, originalPath, pageType }: Props) => {
	const { locale } = useIntl();
	const [isOpenMobileMenu, setIsOpenMobileMenu] = useState(false);
	const [openDropdown, setOpenDropdown] = useState('');
	const [isOpen, setOpen] = useState(false);
	const { store } = useContext(StateContext);
	const { isLoginDisabled } = store;

	const documentIsDefined = typeof document === 'object';

	const toggleMenu = () => {
		if (documentIsDefined) {
			const body = document.querySelector('body');
			if (body) {
				body.classList.toggle('overflowHidden');
			}
		}
		setIsOpenMobileMenu(!isOpenMobileMenu);
	};

	const toggleDropdown = (name: string) => {
		setOpenDropdown(name);
		setOpen(!isOpen);

		if (isOpen) {
			setOpenDropdown(name);
			setOpen(true);
		}

		if (name === openDropdown) setOpen(!isOpen);
	};

	const panelProps = useSpring({
		right: isOpenMobileMenu ? '0%' : '-120%',
	});

	const removeOverflowHidden = useCallback(() => {
		if (documentIsDefined) {
			const body = document.querySelector('body');
			if (body) {
				body.classList.remove('overflowHidden');
			}
		}
	}, [documentIsDefined]);

	const removeOverflowClassAndChangeState = useCallback(() => {
		removeOverflowHidden();
		setIsOpenMobileMenu(false);
	}, [removeOverflowHidden]);

	useEffect(() => {
		removeOverflowHidden();

		if (typeof window === 'undefined') {
			return undefined;
		}

		window.addEventListener('resize', removeOverflowClassAndChangeState);
		return () => window.removeEventListener('resize', removeOverflowClassAndChangeState);
	}, [removeOverflowClassAndChangeState, removeOverflowHidden]);

	const burgerMenu = (
		<svg
			onClick={() => toggleMenu()}
			className={classnames(style.ham, { [style.openHam]: isOpenMobileMenu })}
			viewBox="20 25 62 60"
			width="80"
		>
			<title>
				<FormattedMessage
					id="imageAlt.menuBar"
					defaultMessage="Three thin lines which indicates burger menu."
				/>
			</title>
			<path
				className={classnames(style.line, style.top)}
				d="m 31,43 h 40 c 5.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20"
			/>
			<path className={classnames(style.line, style.middle, isSafari && style.safari_only)} d="m 30,55 h 40" />
			<path
				className={classnames(style.line, style.bottom)}
				d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20"
			/>
		</svg>
	);

	return (
        <div
			className={classnames(
				isOpenMobileMenu && style.openBurger,
				style.burgerMenu,
				locale === Language.Arabic && style.rightMargin
			)}
		>
			{burgerMenu}
			<animated.div
				style={panelProps}
				className={classnames(style.mobileNav, { [style.open]: isOpenMobileMenu })}
			>
				<Link to={routes[locale]['/']} className={style.logo}>
					{icon.logo}
					<p>
						<FormattedMessage
							id="linkDescription.logo"
							defaultMessage="Bankera logo leads you to homepage"
						/>
					</p>
				</Link>
				<div className={locale === Language.Arabic ? style.rightToLeft : style.leftToRight}>
					<nav aria-label="Main">
						<ul className={style.mobileLinksList}>
							{navigation.map((section: NavigationSection) => {
								const { title, links } = section;
								const dropdownId = title.id;
								return (
									<li
										key={dropdownId}
										className={classnames(style.navLink, {
											[style.linkStyleForPolishLang]: locale === Language.Polish,
											[style.linkStyleForArabicLang]: locale === Language.Arabic,
										})}
									>
										<button className={style.openButton}>
											<span
												onClick={() => toggleDropdown(dropdownId)}
												className={style.sublinkTitle}
											>
												<FormattedMessage {...title} />
											</span>
											<svg
												className={classnames(
													style.arrow,
													openDropdown === dropdownId && isOpen && style.rotate180,
													locale === Language.Arabic && style.arrowMarginForArabicLang
												)}
												xmlns="http://www.w3.org/2000/svg"
												width="11"
												height="7"
												viewBox="0 0 11 7"
											>
												<path d="M6.432 6.114a1 1 0 0 1-1.519 0L1.087 1.651A1 1 0 0 1 1.847 0h7.651a1 1 0 0 1 .76 1.65z" />
											</svg>
										</button>
										<ul
											className={classnames(style.mobileSubMenu, {
												[style.toggleMobileSubmenu]: openDropdown === dropdownId && isOpen,
											})}
											style={
												openDropdown === dropdownId && isOpen
													? { padding: `0 0 ${36 * links.length}px` }
													: {}
											}
										>
											{links &&
												links.map((linkItem: NavigationLink) => (
														linkItem && (
															<li
																key={
																	linkItem.link ??
																	linkItem.externalLink ??
																	`mobile-nav-${linkItem.title.id}`
																}
																className={classnames({
																	[style.textRight]: locale === Language.Arabic,
																})}
															>
																{linkItem.link && (
																	<Link
																		to={
																			isIE || isEdge
																				? encodeURI(linkItem.link)
																				: linkItem.link
																		}
																		className={style.link}
																		onClick={() => toggleMenu()}
																	>
																		<FormattedMessage {...linkItem.title} />
																	</Link>
																)}
																{linkItem.externalLink && (
																	<a
																		href={linkItem.externalLink}
																		target="_blank"
																		rel="noopener noreferrer"
																		className={style.link}
																	>
																		<FormattedMessage {...linkItem.title} />
																	</a>
																)}
															</li>
														)
													))}
										</ul>
									</li>
								);
							})}
						</ul>
					</nav>
					{showLanguageSwitcher && <LanguageSwitcher originalPath={originalPath} popover={false} pageType={pageType} />}
					<div className={style.subscribe}>
						<a href={ibLogin} className={classnames(style.button, {
							[style.disabledButton]: isLoginDisabled,
						})}>
							<FormattedMessage id="button.login" defaultMessage="Log in" />
						</a>
						<a
							href={ibSignUp}
							className={classnames(style.filledButton, style.button, {
								[style.rightMargin]: locale === Language.Arabic,
								[style.disabledButton]: isLoginDisabled,
							})}
						>
							<FormattedMessage id="button.signUp" defaultMessage="Sign up" />
						</a>
					</div>
					<h3 className={style.heading}>
						<FormattedMessage id="nav.ecosystem" defaultMessage="Ecosystem" />
					</h3>
					<ul
						className={classnames(style.show, style.mb40)}
					>
						{externalProducts.map(
							(item: ExternalProductsLink) =>
								item && (
									<li key={item.link} className={style.optionsListItem}>
										<a href={item.link} target="_blank" rel="noopener noreferrer">
											<div>
												<span
													className={classnames(style.linkIcon, {
														[style.floatRight]: locale === Language.Arabic,
													})}
												>
													<GatsbyImage image={item.icon} alt="item icon" />
												</span>
												<p
													className={classnames(style.linkTitle, {
														[style.marginForArabicLang]: locale === Language.Arabic,
													})}
												>
													{item.title}
												</p>
												<p
													className={classnames(style.linkSubtitle, {
														[style.marginForArabicLang]: locale === Language.Arabic,
													})}
												>
													{item.subtitle}
												</p>
											</div>
										</a>
									</li>
								)
						)}
					</ul>
				</div>
			</animated.div>
		</div>
    );
};

export default MobileNav;
